/**
 * Storage Adapters
 * Implementations of StorageAdapter interface for different storage backends
 * @module @infra/storage
 */

import type { StorageAdapter, CookieOptions } from './storage.types';

/**
 * LocalStorage adapter
 * Persistent storage in browser (cleared manually or by user)
 */
export class LocalStorageAdapter implements StorageAdapter {
  getItem<T>(key: string, defaultValue: T): T {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Failed to get localStorage["${key}"]`, error);
      return defaultValue;
    }
  }

  setItem<T>(key: string, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Failed to set localStorage["${key}"]`, error);
    }
  }

  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove localStorage["${key}"]`, error);
    }
  }

  clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Failed to clear localStorage', error);
    }
  }

  hasItem(key: string): boolean {
    return localStorage.getItem(key) !== null;
  }

  getAllKeys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) keys.push(key);
    }
    return keys;
  }
}

/**
 * SessionStorage adapter
 * Temporary storage (cleared when tab closes)
 */
export class SessionStorageAdapter implements StorageAdapter {
  getItem<T>(key: string, defaultValue: T): T {
    try {
      const item = sessionStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Failed to get sessionStorage["${key}"]`, error);
      return defaultValue;
    }
  }

  setItem<T>(key: string, value: T): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Failed to set sessionStorage["${key}"]`, error);
    }
  }

  removeItem(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove sessionStorage["${key}"]`, error);
    }
  }

  clear(): void {
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error('Failed to clear sessionStorage', error);
    }
  }

  hasItem(key: string): boolean {
    return sessionStorage.getItem(key) !== null;
  }

  getAllKeys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) keys.push(key);
    }
    return keys;
  }
}

/**
 * Cookie adapter
 * Persistent storage with expiration, shared across tabs
 */
export class CookieAdapter implements StorageAdapter {
  getItem<T>(key: string, defaultValue: T): T {
    try {
      const value = this.getCookie(key);
      return value ? JSON.parse(decodeURIComponent(value)) : defaultValue;
    } catch (error) {
      console.warn(`Failed to get cookie["${key}"]`, error);
      return defaultValue;
    }
  }

  setItem<T>(key: string, value: T, options?: CookieOptions): void {
    try {
      const encoded = encodeURIComponent(JSON.stringify(value));
      let cookie = `${key}=${encoded}`;

      if (options?.path) cookie += `; Path=${options.path}`;
      if (options?.domain) cookie += `; Domain=${options.domain}`;
      if (options?.expires) cookie += `; Expires=${options.expires.toUTCString()}`;
      if (options?.maxAge) cookie += `; Max-Age=${options.maxAge}`;
      if (options?.secure) cookie += '; Secure';
      if (options?.sameSite) cookie += `; SameSite=${options.sameSite}`;

      document.cookie = cookie;
    } catch (error) {
      console.error(`Failed to set cookie["${key}"]`, error);
    }
  }

  removeItem(key: string): void {
    try {
      document.cookie = `${key}=; Max-Age=0; Path=/`;
    } catch (error) {
      console.warn(`Failed to remove cookie["${key}"]`, error);
    }
  }

  clear(): void {
    console.warn('CookieAdapter.clear() is not supported (security restriction)');
  }

  hasItem(key: string): boolean {
    return this.getCookie(key) !== '';
  }

  getAllKeys(): string[] {
    return document.cookie.split('; ').map((c) => c.split('=')[0]);
  }

  private getCookie(name: string): string {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const c = cookies[i].trim();
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length);
      }
    }
    return '';
  }
}

/**
 * In-memory adapter
 * For testing or SSR environments where localStorage is unavailable
 */
export class MemoryStorageAdapter implements StorageAdapter {
  private data: Map<string, string> = new Map();

  getItem<T>(key: string, defaultValue: T): T {
    try {
      const item = this.data.get(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Failed to get memory["${key}"]`, error);
      return defaultValue;
    }
  }

  setItem<T>(key: string, value: T): void {
    try {
      this.data.set(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Failed to set memory["${key}"]`, error);
    }
  }

  removeItem(key: string): void {
    this.data.delete(key);
  }

  clear(): void {
    this.data.clear();
  }

  hasItem(key: string): boolean {
    return this.data.has(key);
  }

  getAllKeys(): string[] {
    return Array.from(this.data.keys());
  }
}

/**
 * Default adapter instances
 */
export const defaultAdapters = {
  local: new LocalStorageAdapter(),
  session: new SessionStorageAdapter(),
  cookie: new CookieAdapter(),
  memory: new MemoryStorageAdapter(),
};
